import React, { useState, useEffect } from 'react';
import { X, User, Phone, MapPin, Hash, ArrowLeft } from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { addCustomer, addSupplier, searchCustomers } from '../../services/dashboardApi';

const AddPartyModal = ({ isOpen, onClose, onSuccess, initialType = 'customer' }) => {
  const { t } = useLang();
  const [partyType, setPartyType] = useState(initialType);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [gstin, setGstin] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Sync state when prop changes (e.g. switching between Customer/Supplier tabs)
  useEffect(() => {
    if (isOpen) {
      setPartyType(initialType);
    }
  }, [initialType, isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      alert(t(LangKeys.name_required));
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: name.trim(),
        mobile: mobile.trim(),
        address: address.trim(),
        gstin: gstin.trim(),
        type: partyType
      };

      const res = partyType === 'customer' 
        ? await addCustomer(payload) 
        : await addSupplier(payload);

      if (res?.success) {
        let createdParty = res.data || res.customer || res.supplier;
        
        // Fallback: If API doesn't return the ID, search the list for the newly added party
        if (!createdParty || !createdParty.id) {
           try {
             const listRes = await searchCustomers(payload.mobile || payload.name);
             if (listRes?.success && listRes.customers?.length > 0) {
               createdParty = listRes.customers.find(c => 
                 (payload.mobile && c.mobile === payload.mobile) || 
                 (payload.name && c.name === payload.name)
               ) || listRes.customers[0];
             }
           } catch (e) {
             console.error("Failed to fetch new party id", e);
           }
        }
        
        onSuccess(createdParty || payload);
        onClose();
      } else {
        alert(res?.message || t(LangKeys.failed_to_save));
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setIsSaving(false);
    }
  };

  const themeColor = partyType === 'customer' ? '#1D4ED8' : '#15803D';
  const buttonText = partyType === 'customer' ? t(LangKeys.add_customer) : t(LangKeys.add_supplier);

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* HEADER (Flutter Style) */}
        <div style={{...styles.header, background: themeColor}}>
          <button style={styles.backBtn} onClick={onClose}>
            <ArrowLeft size={20} color="#fff" />
          </button>
          <h3 style={styles.headerTitle}>{t(LangKeys.add_party)}</h3>
        </div>

        <div style={styles.scrollContent}>
          {/* NAME FIELD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.name)}</label>
            <div style={styles.inputWrap}>
              <User size={18} color="#94A3B8" style={styles.inputIcon} />
              <input 
                type="text" 
                style={styles.textInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t(LangKeys.name)}
                autoFocus
              />
            </div>
          </div>

          {/* MOBILE FIELD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.mobile_no)}</label>
            <div style={styles.mobileRow}>
              <div style={styles.countryCode}>+91</div>
              <div style={{...styles.inputWrap, flex: 1}}>
                <Phone size={18} color="#94A3B8" style={styles.inputIcon} />
                <input 
                  type="tel" 
                  style={styles.textInput}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder={t(LangKeys.mobile_no)}
                  maxLength={10}
                />
              </div>
            </div>
          </div>

          {/* ADDRESS FIELD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.address_optional)}</label>
            <div style={{...styles.inputWrap, alignItems: 'flex-start', paddingTop: '12px'}}>
              <MapPin size={18} color="#94A3B8" style={{...styles.inputIcon, marginTop: '2px'}} />
              <textarea 
                style={{...styles.textInput, height: '80px', resize: 'none'}}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t(LangKeys.address_optional)}
              />
            </div>
          </div>

          {/* GST FIELD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.gst_optional)}</label>
            <div style={styles.inputWrap}>
              <Hash size={18} color="#94A3B8" style={styles.inputIcon} />
              <input 
                type="text" 
                style={styles.textInput}
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder={t(LangKeys.gst_optional)}
              />
            </div>
          </div>

          {/* PARTY TYPE TOGGLE */}
          <div style={styles.radioGroup}>
            <label style={styles.radioOption} onClick={() => setPartyType('customer')}>
              <div style={{
                ...styles.radioCircle,
                borderColor: partyType === 'customer' ? '#1D4ED8' : '#CBD5E1'
              }}>
                {partyType === 'customer' && <div style={styles.radioInner} />}
              </div>
              <span style={styles.radioLabel}>{t(LangKeys.customer_name)}</span>
            </label>

            <label style={styles.radioOption} onClick={() => setPartyType('supplier')}>
              <div style={{
                ...styles.radioCircle,
                borderColor: partyType === 'supplier' ? '#1D4ED8' : '#CBD5E1'
              }}>
                {partyType === 'supplier' && <div style={styles.radioInner} />}
              </div>
              <span style={styles.radioLabel}>{t(LangKeys.add_supplier)}</span>
            </label>
          </div>
        </div>

        {/* BOTTOM ACTION */}
        <div style={styles.footer}>
          <button 
            style={{...styles.saveBtn, backgroundColor: themeColor}}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? '...' : buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 },
  modal: { width: '100%', maxWidth: '450px', background: '#F8FAFC', borderRadius: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' },
  
  header: { height: '60px', padding: '0 16px', display: 'flex', alignItems: 'center', gap: '16px' },
  backBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' },
  headerTitle: { fontSize: '18px', fontWeight: '600', color: '#fff', margin: 0 },
  
  scrollContent: { padding: '20px', overflowY: 'auto', flex: 1 },
  
  inputGroup: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px', marginLeft: '4px' },
  
  inputWrap: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '0 16px', height: '54px' },
  inputIcon: { marginRight: '12px', flexShrink: 0 },
  textInput: { border: 'none', fontSize: '16px', fontWeight: '500', color: '#1E293B', width: '100%', outline: 'none', background: 'transparent' },
  
  mobileRow: { display: 'flex', gap: '10px' },
  countryCode: { width: '60px', height: '54px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '600', color: '#1E293B' },
  
  radioGroup: { display: 'flex', gap: '24px', padding: '10px 4px' },
  radioOption: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
  radioCircle: { width: '20px', height: '20px', borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  radioInner: { width: '10px', height: '10px', borderRadius: '50%', background: '#1D4ED8' },
  radioLabel: { fontSize: '15px', fontWeight: '600', color: '#374151' },
  
  footer: { padding: '16px', background: '#fff', borderTop: '1px solid #E2E8F0' },
  saveBtn: { width: '100%', height: '54px', borderRadius: '12px', border: 'none', color: '#fff', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'opacity 0.2s' }
};

export default AddPartyModal;
